<template>
    <div v-if="place" class="place-detail-page page">
        <div class="container">
            <NavigationsBreadcrumb :items="breadcrumbs" class="mt-0" />

            <h1 class="pt-2">{{ $t(`pickupPlace.${placeType}`) }}: {{ place.contact.city }} ({{ place.name }})</h1>

            <div class="row mb-5">
                <div class="place-detail-page__side col-12 col-md-6 pe-md-3">
                    <h2>{{ $t('pickupPlace.pickupOrder') }}</h2>

                    <div class="fwb">
                        {{ place.contact.city }} ({{ place.name }})
                        <br />
                    </div>
                    <div>{{ place.contact.street }}, {{ place.contact.zip }} {{ place.contact.city }}</div>

                    <a
                        class="btn btn-primary mt-3 mb-2 col-12 col-md-8"
                        :href="$t('pickupPlace.location') + place.location.latitude + ',' + place.location.longitude"
                        target="_blank"
                    >
                        <i class="ico ico-map-place-geo"></i>
                        {{ $t('pickupPlace.showOnMap') }}
                    </a>

                    <h2 v-if="place.contact.parkingInfo || place.contact.placeDetailed" class="mt-4">
                        {{ $t('pickupPlace.whereBox') }}
                    </h2>

                    <h3 v-if="place.contact.parkingInfo" class="text-brand">
                        {{ $t('pickupPlace.parkingInfo') }}
                    </h3>
                    <div v-if="place.contact.parkingInfo">{{ place.contact.parkingInfo }}</div>

                    <h3 v-if="place.contact.placeDetailed" class="text-brand mt-3">
                        {{ $t('pickupPlace.boxLocation') }}
                    </h3>
                    <div v-if="place.contact.placeDetailed">{{ place.contact.placeDetailed }}</div>

                    <div v-if="$device.isMobile && place.images.length > 0" class="gallery">
                        <Carousel ref="carousel" v-model="slide" :wrap-around="false" class="slide">
                            <Slide v-for="(image, index) in place.images" :key="index" class="slide">
                                <img
                                    :src="image.url"
                                    :alt="image.description"
                                    class="rounded cursor-point"
                                    loading="lazy"
                                    height="375"
                                    @click="showPhotoModal(index)"
                                />
                            </Slide>
                        </Carousel>
                        <a href="#" role="button" :class="slide === 0 ? 'd-none' : ''" @click.prevent="prevSlide">
                            <span aria-hidden="true" class="carousel-control-prev-icon"></span>
                        </a>
                        <a href="#" role="button" :class="slide + 1 === place.images.length ? 'd-none' : ''" @click.prevent="nextSlide">
                            <span aria-hidden="true" class="carousel-control-next-icon"></span>
                        </a>
                    </div>

                    <h2 class="mt-4">
                        {{ $t('pickupPlace.openingHours') }}
                    </h2>

                    <div v-for="(hours, day) in place.openingTime" v-show="openingHoursShown" :key="day">
                        <div
                            v-if="day !== 'exception'"
                            class="place-detail-page__day-row"
                            :style="[
                                ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].indexOf(day) % 2 === 0
                                    ? { background: 'rgba(246, 246, 246, 1)' }
                                    : '',
                                getDayName() === day ? { 'font-weight': '590' } : '',
                            ]"
                        >
                            <div class="col-4" v-html="`${$t(`days.${day}`)}`"></div>
                            <div class="col-4" v-html="`${hours || $t('pharmacy.closed')}`"></div>
                            <div v-if="getDayName() === day" class="d-flex col-4">
                                <div class="place-detail-page__circle" :class="place.isOpen ? 'ico-is-opened' : 'ico-is-closed'"></div>
                                <div :class="place.isOpen ? 'text-success' : 'text-danger'" class="place-detail-page__circle-text">
                                    <div v-if="place.isOpen">
                                        {{ $t('pickupPlace.opened') }}
                                    </div>
                                    <div v-else>
                                        {{ $t('pickupPlace.closed') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-show="!openingHoursShown">
                        <div class="place-detail-page__day-row place-detail-page__day-row__small">
                            <div class="col-4" v-html="$t('pickupPlace.today')"></div>
                            <div class="col-4" v-html="`${place.openingTime[getDayName()] || $t('pharmacy.closed')}`"></div>
                            <div class="d-flex col-4">
                                <div class="place-detail-page__circle" :class="place.isOpen ? 'ico-is-opened' : 'ico-is-closed'"></div>
                                <div :class="place.isOpen ? 'text-success' : 'text-danger'" class="place-detail-page__circle-text">
                                    <div v-if="place.isOpen">
                                        {{ $t('pickupPlace.opened') }}
                                    </div>
                                    <div v-else>
                                        {{ $t('pickupPlace.closed') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <a
                        class="place-detail-page__opening-hours-button"
                        @click="openingHoursShown = !openingHoursShown"
                        v-html="openingHoursShown ? $t('pharmacy.hideFullOpeningHours') : $t('pharmacy.showFullOpeningHours')"
                    ></a>

                    <div v-if="place.openingTime.exception" class="place-detail-page__day-exception">
                        {{ place.openingTime.exception }}
                    </div>

                    <h2 class="mt-4">
                        {{ $t('pickupPlace.howOpenBox') }}
                    </h2>
                    <div>{{ $t('pickupPlace.boxInstructions') }}</div>

                    <h2 class="mt-4">
                        {{ $t('pickupPlace.openingTrouble') }}
                    </h2>
                    <div v-html="$t('pickupPlace.anyProblem')"></div>

                    <a class="btn col-12 col-md-8 place-detail-page__cc-button" :href="$t('pickupPlace.anyProblemPhone')">
                        <i class="ico ico-map-place-phone"></i>
                        {{ $t('pickupPlace.anyProblemCall') }}
                    </a>

                    <a class="btn col-12 col-md-8 place-detail-page__cc-button" :href="$t('pickupPlace.anyProblemEmail')">
                        <i class="ico ico-map-place-email"></i>
                        {{ $t('pickupPlace.anyProblemWrite') }}
                    </a>
                </div>
                <div class="place-detail-page__side place-detail-page__side__map col-12 col-md-6">
                    <div v-if="$device.isDesktop && place.images.length > 0" class="gallery">
                        <div>
                            <img :src="previewImageUrl" :alt="previewImageAlt" loading="lazy" class="place-detail-page__main-photo" />
                        </div>

                        <div v-if="place.images.length > 1" class="row">
                            <img
                                v-for="(image, index) in place.images"
                                :key="index"
                                :src="image.url"
                                :alt="image.description"
                                loading="lazy"
                                class="place-detail-page__photo"
                                @click="showPhotoInPreview(index)"
                            />
                        </div>
                    </div>

                    <div class="place-detail-page__map-container">
                        <LMap
                            ref="map"
                            :zoom="16"
                            :min-zoom="3"
                            :center="[place.location.latitude, place.location.longitude]"
                            :use-global-leaflet="false"
                            :options="{ zoomControl: false }"
                        >
                            <LControl position="bottomleft">
                                <a href="https://mapy.cz/" target="_blank">
                                    <img src="https://api.mapy.cz/img/api/logo.svg" alt="Mapy.cz" height="30" />
                                </a>
                            </LControl>
                            <LTileLayer
                                :url="`https://api.mapy.cz/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${mapyCzApiKey}`"
                                :attribution="mapsAttribution"
                                layer-type="base"
                                name="OpenStreetMap"
                            />

                            <LMarker :lat-lng="[place.location.latitude, place.location.longitude]">
                                <LIcon
                                    :icon-url="`https://www.pilulka.cz/nuxt-assets/icons/map-ico-${placeType}.png`"
                                    :icon-size="[30, 43]"
                                />
                            </LMarker>

                            <LControlZoom position="bottomright" />
                        </LMap>
                    </div>
                </div>
            </div>
        </div>

        <Modal modal-name="place-photo-modal" modal-class="modal-max" :title="place.name">
            <Carousel ref="carousel" v-model="slide" :wrap-around="false">
                <Slide v-for="image in place.images" :key="image.url">
                    <img :src="image.url" :alt="image.description" class="rounded main-image" loading="lazy" height="790" />
                </Slide>
            </Carousel>
            <a ref="carouselPrev" href="#" role="button" :class="slide === 0 ? 'd-none' : ''" @click.prevent="prevSlide">
                <span aria-hidden="true" class="carousel-control-prev-icon"></span>
            </a>
            <a
                ref="carouselNext"
                href="#"
                role="button"
                :class="slide + 1 === place.images.length ? 'd-none' : ''"
                @click.prevent="nextSlide"
            >
                <span aria-hidden="true" class="carousel-control-next-icon"></span>
            </a>
        </Modal>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { Carousel, Slide } from 'vue3-carousel'
import { useNuxtApp } from '#app'
import { useRoute } from '#imports'
import PickupPlace from '~/models/Api/Cart/PickupPlace'
import BreadcrumbItem from '~/models/BreadcrumbItem'
import PilulkaHeadInfo from '~/models/Head/PilulkaHeadInfo'
import getDayName from '~/extensions/getDayName'
import Modal from '~/components/Modal.vue'
import 'vue3-carousel/dist/carousel.css'

const mapyCzApiKey = useRuntimeConfig().public.mapyCz.apiKey
const mapsAttribution = '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>'

const route = useRoute()
const params = route.matched[0].props.default as { [name: string]: string }
const placeId = parseInt(params.id)
const nuxtApp = useNuxtApp()
const breadcrumbs = ref<BreadcrumbItem[]>([])
const place = ref<PickupPlace | null>(null)
const placeType = ref<string>('pilulka_box')
const shippingType = ref<string>('DEL_PILULKA_BOX')
const slide = ref(0)
const openingHoursShown = ref(false)

const response = await nuxtApp.$repository.viewPickupPlace(placeId, shippingType.value)
place.value = response.response ?? null

const previewImageUrl = ref<string>(place.value?.images[0]?.url ?? '')
const previewImageAlt = ref<string>(place.value?.images[0]?.description ?? '')
const showPhotoInPreview = (index: number) => {
    previewImageUrl.value = place.value?.images[index]?.url ?? ''
    previewImageAlt.value = place.value?.images[index]?.description ?? ''
}

const i18n = useI18n()
breadcrumbs.value = [
    {
        title: i18n.t('pickupPlace.mapTitle'),
        url: i18n.t('pickupPlace.mapLink') + placeType.value,
    },
    {
        title: place.value?.name ?? '',
        url: route.path,
    },
]

const config = useRuntimeConfig()
const headInfo = route.meta.headInfo as PilulkaHeadInfo
const defaultCanonical = `${config.public.apiUrl}${place.value?.url ?? ''}`
useHead({
    title: headInfo.title !== '' ? headInfo.title : place.value?.name ?? '',
    bodyAttrs: {
        'data-no-cache': true,
    },
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: place.value?.contact.placeDetailed ?? '',
        },
    ],
    link: [
        {
            rel: 'canonical',
            href: headInfo.canonical !== '' ? headInfo.canonical : defaultCanonical,
        },
    ],
})

const showPhotoModal = (activeSlide: number) => {
    slide.value = activeSlide
    useNuxtApp().$bus.$emit('showModal', 'place-photo-modal')
}

const prevSlide = () => {
    slide.value--

    if (slide.value < 0) {
        slide.value = 0
    }
}

const nextSlide = () => {
    slide.value++

    if (slide.value >= (place.value?.images.length ?? 1)) {
        slide.value = (place.value?.images.length ?? 1) - 1
    }
}
</script>

<style lang="scss" scoped>
.carousel-control-prev-icon,
.carousel-control-next-icon {
    width: 60px;
    height: 60px;
    position: absolute;
    z-index: 999;
    top: calc(50% - 32px);
}

.carousel-control-next-icon {
    right: 0;
}

.gallery {
    position: relative;
    margin-bottom: 30px;

    .carousel-control-prev-icon,
    .carousel-control-next-icon {
        width: 50px;
        height: 50px;
        top: calc(50% - 15px);
    }
}

.place-detail-page {
    margin-top: 30px;

    &__side {
        display: flex;
        flex-direction: column;

        &__map {
            @include screen-md {
                padding-left: 1rem;
            }
        }
    }

    &__day-exception {
        margin-top: 15px;
        padding: 1rem;
        border-radius: $rounded;
        background: $color-orange-light;
        color: $color-orange-dark;
        font-size: 14px;
    }

    &__day-row {
        display: flex;
        padding: 20px;

        &__small {
            font-weight: 590;
            background: rgba(246, 246, 246, 1);
        }
    }

    &__opening-hours-button {
        margin-top: 15px;
        text-align: center;
        text-decoration: underline;
        cursor: pointer;
    }

    &__photo {
        width: 150px;
        height: 100px;
        cursor: pointer;
    }

    &__main-photo {
        height: 400px;
        width: 600px;
        margin-bottom: 15px;
    }

    &__cc-button {
        border-color: $color-dark;
        margin-top: 15px;
    }

    #js-place-detail-map {
        width: 100%;
        height: 400px;
        margin-top: 20px;
    }

    &__circle {
        height: 15px;
        width: 15px;
        border-radius: 50%;
        display: inline-block;
    }

    &__circle-text {
        display: inline-block;
        font-size: 16px;
        font-weight: bold;
        margin-left: 10px;
    }

    &__badge {
        padding: 0.25rem 0.5rem;
        margin-left: 0.5rem;
        text-transform: uppercase;
        border-radius: 5px;
    }

    .ico-is-opened {
        background-color: #4eae33;
    }

    .ico-is-closed {
        background-color: $color-grey-semi-dark;
    }

    .ico-map-place-geo {
        height: 22px;
        width: 16px;
        background-image: url('/nuxt-assets/icons/map-ico-white.svg');
        margin: -6px 3px;
    }

    .ico-map-place-phone {
        height: 22px;
        width: 16px;
        background-image: url('/nuxt-assets/icons/map-ico-phone-black.svg');
        margin: -6px 3px;
    }

    .ico-map-place-email {
        height: 22px;
        width: 16px;
        background-image: url('/nuxt-assets/icons/map-ico-email-black.svg');
        margin: -6px 3px;
    }

    &__map-container {
        margin-top: 20px;
        position: relative;
        height: 400px;
        z-index: 1;
    }
}
</style>
